import React, { useState, useEffect, useMemo } from 'react';
import { ArrowLeft, Search, ChevronDown, ChevronUp, MessageCircle, Mail, HelpCircle, User, Store } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { getUserChosenMunicipio } from '../services/userChosenMunicipio';

const AUDIENCES = [
  { id: 'consumidor', title: 'Para Consumidores', icon: User },
  { id: 'empreendedor', title: 'Para Empreendedores', icon: Store },
];

function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-4 flex items-center justify-between text-left hover:text-[var(--primary)] transition-colors"
        data-testid="faq-item-toggle"
      >
        <span className="font-medium pr-4">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 flex-shrink-0 text-[var(--primary)]" />
        ) : (
          <ChevronDown className="w-5 h-5 flex-shrink-0 text-gray-400" />
        )}
      </button>
      {isOpen && (
        <div className="pb-4 pr-8">
          <p className="text-gray-600 leading-relaxed whitespace-pre-line">{answer}</p>
        </div>
      )}
    </div>
  );
}

export default function Ajuda() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [audience, setAudience] = useState('consumidor');
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(null);

  const municipio = getUserChosenMunicipio ? getUserChosenMunicipio() : null;

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (municipio?.id) params.municipio_id = municipio.id;
    // Conteúdo gerenciado no painel admin (FAQ)
    api.get(`/faq/${audience}`, { params })
      .then((r) => {
        const gs = r.data?.grupos || [];
        setGrupos(gs);
        setActiveCategory(gs[0]?.category || null);
      })
      .catch(() => setGrupos([]))
      .finally(() => setLoading(false));
  }, [audience, municipio?.id]);

  const filteredGrupos = useMemo(() => {
    if (!searchQuery.trim()) return grupos;
    const q = searchQuery.trim().toLowerCase();
    return grupos
      .map((g) => ({
        ...g,
        items: (g.items || []).filter(
          (i) =>
            (i.question || '').toLowerCase().includes(q) ||
            (i.answer || '').toLowerCase().includes(q)
        ),
      }))
      .filter((g) => g.items.length > 0);
  }, [grupos, searchQuery]);

  const displayGrupos = filteredGrupos;
  const currentGroup =
    displayGrupos.find((g) => g.category === activeCategory) || displayGrupos[0];

  return (
    <div className="min-h-screen bg-[var(--background)] pb-20">
      {/* Header */}
      <div className="bg-white border-b border-[var(--border)]">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[var(--primary)] hover:underline mb-6"
            data-testid="ajuda-voltar-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            Voltar
          </button>
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope' }}>
            Central de Ajuda
          </h1>
          <p className="text-gray-600 mb-6">
            Encontre respostas para suas dúvidas
          </p>

          {/* Audience Tabs */}
          <div className="flex gap-2 mb-4">
            {AUDIENCES.map((a) => {
              const Icon = a.icon;
              return (
                <button
                  key={a.id}
                  onClick={() => { setAudience(a.id); setSearchQuery(''); }}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    audience === a.id
                      ? 'bg-[var(--primary)] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid={`ajuda-audience-${a.id}`}
                >
                  <Icon className="w-4 h-4" />
                  {a.title}
                </button>
              );
            })}
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por dúvidas..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-xl border border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)]"
              data-testid="ajuda-search-input"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {loading ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center text-gray-500">
            Carregando conteúdo…
          </div>
        ) : (
          <div className="flex flex-col md:flex-row gap-8">
            {/* Category Sidebar */}
            <div className="md:w-64 flex-shrink-0">
              <div className="bg-white rounded-xl shadow-sm p-4 sticky top-20">
                <h3 className="font-semibold text-sm text-gray-500 uppercase mb-3">Categorias</h3>
                <nav className="space-y-1">
                  {displayGrupos.map((g) => (
                    <button
                      key={g.category}
                      onClick={() => setActiveCategory(g.category)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                        (currentGroup?.category === g.category)
                          ? 'bg-[var(--primary)]/10 text-[var(--primary)] font-medium'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                      data-testid={`ajuda-cat-${g.category}`}
                    >
                      <HelpCircle className="w-5 h-5" />
                      <span className="text-sm">{g.category}</span>
                      <span className="ml-auto text-xs text-gray-400">
                        {(g.items || []).length}
                      </span>
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* FAQ Content */}
            <div className="flex-1">
              {currentGroup && (
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h2 className="text-xl font-bold mb-6 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                    <HelpCircle className="w-5 h-5 text-[var(--primary)]" />
                    {currentGroup.category}
                  </h2>
                  <div className="divide-y divide-gray-100">
                    {(currentGroup.items || []).map((item) => (
                      <FAQItem key={item.id} question={item.question} answer={item.answer} />
                    ))}
                  </div>
                </div>
              )}

              {displayGrupos.length === 0 && (
                <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                  <HelpCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">
                    {searchQuery
                      ? `Nenhum resultado encontrado para "${searchQuery}"`
                      : 'Nenhum conteúdo de ajuda publicado ainda.'}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Contact Section */}
        <div className="mt-12 bg-gradient-to-r from-purple-100 to-pink-100 rounded-2xl p-8 text-center">
          <h3 className="text-xl font-bold mb-2">Não encontrou o que procurava?</h3>
          <p className="text-gray-600 mb-6">
            Entre em contato conosco e responderemos o mais rápido possível.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:contato@empreendecomunidade.com.br"
              className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-50 rounded-full px-6 py-3 font-medium shadow-sm transition-all"
            >
              <Mail className="w-5 h-5 text-[var(--primary)]" />
              Enviar e-mail
            </a>
            <a
              href="https://wa.me/5511999999999"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white rounded-full px-6 py-3 font-medium shadow-sm transition-all"
            >
              <MessageCircle className="w-5 h-5" />
              WhatsApp
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
